import React from "react";
import PricingCard from "../components/PricingCard";


const introText =
  `Maternity and newborn photography captures two of life's most precious moments—the anticipation of new life and the first days with your baby. These sessions celebrate the beauty of pregnancy and the tender early moments with your newborn. Whether you're expecting or have just welcomed your little one, I create timeless images that honor these fleeting and precious chapters. Sessions can be done indoors or outdoors, tailored to your comfort and style, preserving memories you'll treasure forever.`;

const MaternityPortfolio = () => {
  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="text-center py-20 px-5 bg-gradient-to-br from-slate-50 via-white to-teal-50/30 relative overflow-hidden">
          {/* Animated gradient mesh - matching HeroSection */}
          <div className="absolute inset-0 opacity-40">
            <div
              style={{
                backgroundImage: `
                  radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.8) 0%, transparent 60%),
                  radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.7) 0%, transparent 60%),
                  radial-gradient(circle at 40% 40%, rgba(16, 185, 129, 0.6) 0%, transparent 60%)
                `,
              }}
              className="absolute inset-0"
            ></div>
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            <h1 className="text-3xl md:text-5xl font-bold mb-5 text-gray-900">
              Maternity and Newborn Photography
            </h1>
            <h3 className="text-lg md:text-xl text-teal-600 font-light">Celebrating the beauty of motherhood and new life</h3>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 px-5 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl mx-auto mb-16 text-center">
              <p className="text-gray-600 leading-relaxed text-base md:text-lg">
                {introText}
              </p>
            </div>

            <div className="flex flex-col gap-16 md:gap-32 max-w-5xl mx-auto">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex flex-col md:items-center gap-8 md:gap-12 py-8 md:py-12 ${
                    step.imagePosition === "right"
                      ? "md:flex-row"
                      : "md:flex-row-reverse"
                  }`}
                >
                  <div className="flex-shrink-0 w-full md:w-96 h-96 md:h-[500px] relative mb-8 md:mb-0">
                    <div
                      className={`absolute top-0 ${
                        step.imagePosition === "right" ? "left-0" : "right-0"
                      } w-32 md:w-56 h-full bg-teal-600 rounded-lg z-0`}
                    ></div>
                    <div
                      className={`absolute top-12 md:top-16 ${
                        step.imagePosition === "right" ? "left-8 md:left-16" : "right-8 md:right-16"
                      } w-64 md:w-80 h-96 md:h-[500px] rounded-lg overflow-hidden shadow-2xl z-10 bg-white`}
                    >
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover "
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="text-4xl md:text-6xl font-bold text-teal-600 mb-3 md:mb-4">
                      {step.number}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-semibold mb-3 md:mb-4 text-gray-800">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        <div className="bg-gradient-to-r from-teal-500 to-teal-600">
          <h1 className="text-center text-2xl md:text-3xl font-bold py-8 text-white px-4">
            Choose your Ideal Plan
          </h1>
          <PricingCard plans={pricingPlans} category="maternity" />
        </div>
      </div>
    </>
  );
};

const pricingPlans = [
  {
    title: "Bloom",
    price: "€225",
    features: [
      "1 Hour Session",
      "Indoor or Outdoor Location",
      "10 Edited Photos",
      "Online Gallery",
    ],
  },
  {
    title: "Blossom",
    price: "€250",
    features: [
      "1.5 Hour Session",
      "Indoor or Outdoor Location",
      "2 Outfit Changes",
      "20 Edited Photos",
      "Online Gallery",
    ],
  },
  {
    title: "Flourish",
    price: "€350",
    features: [
      "2 Hour Sessions each",
      "Maternity and Newborn Session",
      "2 Outfit Changes for Maternity",
      "Partner/Family Included",
      "30 Edited Photos",
      "Online Gallery"
    ],
  },
];



const steps = [
  {
    number: "01",
    title: "Planning Your Session",
    description:
      `We offer both maternity and newborn photography sessions, available indoors or outdoors based on your preference. For maternity photos, the ideal time is between 28-34 weeks when your bump is beautifully visible. For newborn sessions, the best time is within the first 2 weeks after birth when babies are sleepiest and most flexible for those adorable curled-up poses. During our consultation, we'll discuss your vision, preferred style, and location options. We'll talk about outfit choices, props, and whether you'd like to include your partner or older siblings. I'll help you choose the perfect setting that matches your style and comfort level.`,
    image:
      "https://res.cloudinary.com/shaszstudios-photography/image/upload/v1763464873/yawning_1_fa1iex.jpg",
    imagePosition: "right",
  },
  {
    number: "02",
    title: "During the Session",
    description:
      `Your comfort and your baby's safety are my top priorities throughout the session. For maternity shoots, I'll guide you through gentle poses that flatter your beautiful bump and capture the glow of pregnancy. For newborn sessions, I work patiently and gently, taking breaks for feeding and soothing as needed. The session is relaxed and unhurried—there's no rush. I capture both intimate close-ups and full portraits that showcase this special time. If family members are joining, I'll include beautiful moments that celebrate your growing or new family. Sessions typically last 1-2 hours for maternity and 2-3 hours for newborns to allow plenty of time for breaks.`,
    image:
      "https://res.cloudinary.com/shaszstudios-photography/image/upload/v1763243691/maternity_couple_filmy_image_wqposl.jpg",
    imagePosition: "left",
  },
  {
    number: "03",
    title: "After the Session",
    description:
      `Within one week, you'll receive a preview gallery with highlights from your session. I carefully edit each selected photo to enhance the natural beauty while keeping the authentic feel. Your complete gallery of high-resolution edited photos will be ready within 2-3 weeks. All images are delivered through a beautiful online gallery where you can view, download, and share your photos. You'll have full printing rights, so you can create albums, prints, or wall art to treasure these memories forever. These photos become cherished keepsakes as your family grows and you look back on these beautiful early moments.`,
    image:
      "https://res.cloudinary.com/shaszstudios-photography/image/upload/v1762207667/maternity_2_edit_3_q4saiy.jpg",
    imagePosition: "right",
  },
];

export default MaternityPortfolio;
